import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { ApartmentsModal } from '../Sections/ApartmentsModal';
import { useApartmentsFilter } from '../../../hooks/useApartmentsFilter';
import { ApartmentFilterCard } from './ApartmentFilterCard';
import { Apartment } from '../../../graphql/graphql';
import {
  ApartmentsFilterContainer,
  ApartmentsFilterContent,
  ApartmentsFilterTitle,
  PaginationWrapper
} from './styled';
import { ResidenceRoomsFilter } from '../Sections/ItemsSection/ResidenceRoomsFilter';
import { PhoneButtonClickHandler } from '../BottomPhoneButton/types';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';
import Pagination from '../../ui/Pagination';
import { PaginationVariantEnum } from '../../ui/Pagination/types';

interface ApartmentsFilterProps {
  apartments: Apartment[];
  apartmentRooms?: number[];
  name?: string;
  phones: string[] | null;
  isVisible: boolean;
  currentFilter: string | number;
  setCurrentFilter: React.Dispatch<React.SetStateAction<string | number>>;
  onClickHandler: PhoneButtonClickHandler;
  setIsVisible: (value: boolean) => void;
}

export const ApartmentsFilter = forwardRef<
  {
    resetToFirstPage: () => void;
  },
  ApartmentsFilterProps
>(
  (
    {
      apartments = [],
      apartmentRooms,
      name,
      phones,
      isVisible,
      currentFilter,
      setCurrentFilter,
      onClickHandler,
      setIsVisible
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { isSmallOrLargeDesktop } = useScreenSize();
    const isClient = useClient();
    const planningBlockRef = useRef<HTMLDivElement | null>(null);
    const shouldScrollRef = useRef(false);

    const {
      filteredList,
      paginatedList,
      currentPage,
      totalPages,
      goToPage,
      setCurrentPage
    } = useApartmentsFilter(currentFilter, apartments);

    useImperativeHandle(ref, () => ({
      resetToFirstPage: () => {
        goToPage(1);
      }
    }));

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const description = t('pages.residence.residence_name', {
      residence_name: name
    });

    const apartmentsTitle = t(
      !isSmallOrLargeDesktop || !isClient
        ? 'residences.apartments_title'
        : 'residences.apartments_title_desk',
      {
        name
      }
    );

    useEffect(() => {
      if (!shouldScrollRef.current) return;

      shouldScrollRef.current = false;

      planningBlockRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, [currentPage]);

    const onPaginationChange = (page: number) => {
      shouldScrollRef.current = true;
      setCurrentPage(page);
    };

    return (
      <>
        <ApartmentsFilterContainer id="ap-filter" ref={planningBlockRef}>
          <ApartmentsFilterTitle tag="title_3_new">
            {apartmentsTitle}
          </ApartmentsFilterTitle>

          {!!apartmentRooms?.length && (
            <ResidenceRoomsFilter
              itemsRooms={apartmentRooms}
              currentValue={currentFilter}
              stat="residence-filter"
              onClick={value => {
                setCurrentFilter(value);
              }}
            />
          )}

          <ApartmentsFilterContent>
            {paginatedList?.map(item => (
              <ApartmentFilterCard
                key={item.id}
                item={item}
                setIsModalOpen={setIsModalOpen}
                setSelectedId={setSelectedId}
                imgDescription={description}
              />
            ))}
          </ApartmentsFilterContent>
          {totalPages > 1 && (
            <PaginationWrapper>
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={onPaginationChange}
                variant={PaginationVariantEnum.defaultLight}
              />
            </PaginationWrapper>
          )}
        </ApartmentsFilterContainer>

        {isModalOpen && !!selectedId && (
          <ApartmentsModal
            isOpen={isModalOpen}
            hide={() => setIsModalOpen(false)}
            imgDescription={description}
            phones={phones}
            isVisible={isVisible}
            onClickHandler={onClickHandler}
            setIsVisible={setIsVisible}
            filteredList={filteredList}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
          />
        )}
      </>
    );
  }
);

ApartmentsFilter.displayName = 'ApartmentsFilter';
